'use client'

import React from 'react'
import { cn } from '@/lib/utils'

interface NewsItem {
  id?: string
  title: string
  url?: string | null
  image_url?: string | null
  fetched_at?: string | null
  field?: string | null
}

function timeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) return ''
  try {
    const d = new Date(dateStr)
    const now = new Date()
    const s = Math.floor((now.getTime() - d.getTime()) / 1000)
    if (s < 60) return 'Just now'
    if (s < 3600) return `${Math.floor(s / 60)}m ago`
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`
    if (s < 604800) return `${Math.floor(s / 86400)}d ago`
    return d.toLocaleDateString()
  } catch {
    return ''
  }
}

interface NewsCardWidgetProps {
  item: NewsItem
  className?: string
  onClick?: () => void
}

export function NewsCardWidget({ item, className, onClick }: NewsCardWidgetProps) {
  const bgImage = item.image_url?.trim() || null
  const t = timeAgo(item.fetched_at)

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'relative h-full min-h-0 overflow-hidden rounded-xl border border-border/60 shadow-[0_10px_30px_-24px_rgba(15,23,42,0.55)] transition-[transform,box-shadow,border-color] duration-200 ease-out hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-[0_18px_42px_-26px_rgba(15,23,42,0.55)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 active:translate-y-0 active:scale-[0.99]',
        className
      )}
      aria-label={item.title}
    >
      {bgImage ? (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bgImage})` }}
          />
          <div className="from-zinc-950/88 absolute inset-0 bg-gradient-to-r via-zinc-950/55 to-zinc-950/10" />
        </>
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-muted via-card to-muted/60" />
      )}
      <div className="relative flex h-full min-h-[100px] flex-col justify-end p-3 text-left">
        <p
          className={cn(
            'line-clamp-2 text-sm font-medium leading-tight',
            bgImage ? 'text-white drop-shadow-[0_1px_2px_rgba(24,24,27,0.7)]' : 'text-foreground'
          )}
        >
          {item.title}
        </p>
        {t && (
          <span
            className={cn(
              'mt-1 text-xs',
              bgImage
                ? 'text-white/80 drop-shadow-[0_1px_1px_rgba(24,24,27,0.7)]'
                : 'text-muted-foreground'
            )}
          >
            {t}
          </span>
        )}
      </div>
    </button>
  )
}
